import { SNAPSHOT_RATE, type NetEvent, type SnapshotMessage } from './protocol'

const INTERVAL = 1000 / SNAPSHOT_RATE
/** Older snapshots than this behind the newest are dropped. */
const KEEP_MS = 1000
/** Further than this from where it should be, playback jumps instead of drifting. */
const RESYNC_MS = 250
/** How quickly the playback clock is pulled toward its target, as a time constant. */
const DRIFT_MS = 250
/** Bounds on the interpolation delay; see `delay`. */
const MIN_DELAY = 2 * INTERVAL
const MAX_DELAY = 300

export interface PlaybackFrame {
  /** The snapshot being blended from; null right after a (re)start. */
  from: SnapshotMessage | null
  to: SnapshotMessage
  /** 0 at `from`, 1 at `to`. */
  alpha: number
}

/**
 * Snapshots as they arrive, and the clock they are played back on.
 *
 * The server stamps each snapshot with its own wall clock. Playback runs on
 * that clock, a little behind the newest snapshot — far enough that the next
 * one has normally arrived before it is needed, so what is drawn is always a
 * blend between two real states and never a guess. The delay follows the
 * measured jitter: a steady connection plays closer to the present.
 */
export class SnapshotBuffer {
  private readonly snaps: SnapshotMessage[] = []
  private readonly pending: NetEvent[] = []
  private playTime = Number.NaN
  private lastLocal = Number.NaN
  private jitter = 0
  private lastArrival = Number.NaN
  private lastSent = Number.NaN

  /** How far behind the newest snapshot playback aims to run, in milliseconds. */
  get delay(): number {
    return Math.min(MAX_DELAY, Math.max(MIN_DELAY, INTERVAL + 4 * this.jitter))
  }

  push(snap: SnapshotMessage, localNow: number): void {
    const newest = this.snaps.at(-1)
    if (newest && snap.matchId !== newest.matchId) this.clear()
    if (newest && snap.time <= newest.time) return

    if (!Number.isNaN(this.lastArrival)) {
      // How much the gap between arrivals differs from the gap between sends.
      const deviation = Math.abs(localNow - this.lastArrival - (snap.time - this.lastSent))
      this.jitter += (deviation - this.jitter) * 0.1
    }
    this.lastArrival = localNow
    this.lastSent = snap.time

    this.snaps.push(snap)
    for (const event of snap.events) this.pending.push(event)
    while (this.snaps.length > 2 && this.snaps[0].time < snap.time - KEEP_MS) this.snaps.shift()
  }

  /** The two snapshots around the playback time at `localNow`. */
  frame(localNow: number): PlaybackFrame | null {
    const newest = this.snaps.at(-1)
    if (!newest) return null

    const target = newest.time - this.delay
    const elapsed = Number.isNaN(this.lastLocal) ? 0 : localNow - this.lastLocal
    this.lastLocal = localNow
    if (Number.isNaN(this.playTime) || Math.abs(target - this.playTime) > RESYNC_MS) {
      this.playTime = target
      // A jump — a hidden tab, a stall — makes the queued sounds stale.
      this.dropEventsBefore(target - RESYNC_MS)
    } else {
      this.playTime += elapsed
      this.playTime += (target - this.playTime) * (1 - Math.exp(-elapsed / DRIFT_MS))
    }
    // Never past the newest state: with nothing newer, hold rather than guess.
    this.playTime = Math.min(this.playTime, newest.time)

    const next = this.snaps.findIndex((s) => s.time >= this.playTime)
    if (next <= 0) return { from: null, to: this.snaps[Math.max(0, next)], alpha: 1 }
    const from = this.snaps[next - 1]
    const to = this.snaps[next]
    return { from, to, alpha: (this.playTime - from.time) / (to.time - from.time) }
  }

  /** Events whose time has come, oldest first, removed from the queue. */
  takeEvents(): NetEvent[] {
    let due = 0
    while (due < this.pending.length && this.pending[due].at <= this.playTime) due++
    return this.pending.splice(0, due)
  }

  clear(): void {
    this.snaps.length = 0
    this.pending.length = 0
    this.playTime = Number.NaN
    this.lastArrival = Number.NaN
  }

  private dropEventsBefore(time: number): void {
    let stale = 0
    while (stale < this.pending.length && this.pending[stale].at < time) stale++
    this.pending.splice(0, stale)
  }
}
